import React, { useState } from 'react';

function calculateAge(birthdate) {
  if (!birthdate) return null;
  const birth = new Date(birthdate + 'T00:00:00');
  if (isNaN(birth)) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatNoteDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

/* Reusable note composer + list for each tab */
function NotesTab({ notes, emptyMessage, personId, categoryKey, onNoteAdded }) {
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!noteText.trim()) return;
    setSaving(true);
    setError('');
    fetch('/api/notes', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        person_id: personId,
        note: noteText.trim(),
        category_key: categoryKey,
      }),
    })
      .then(res => res.json())
      .then(data => {
        setSaving(false);
        if (data.success) {
          setNoteText('');
          if (onNoteAdded) onNoteAdded();
        } else {
          setError(data.error || 'Failed to save note');
        }
      })
      .catch(err => {
        setSaving(false);
        setError(err.message || 'Failed to save note');
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit();
    }
  };

  return (
    <div className="modal-body">
      {/* Composer at top */}
      <div className="note-composer">
        <textarea
          className="note-composer-input"
          placeholder="Add a note..."
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          disabled={saving}
        />
        <div className="note-composer-actions">
          {error && <span className="note-composer-error">{error}</span>}
          <span className="note-composer-hint">Ctrl+Enter to save</span>
          <button
            className="note-composer-btn"
            onClick={handleSubmit}
            disabled={saving || !noteText.trim()}
          >
            {saving ? 'Saving...' : 'Add Note'}
          </button>
        </div>
      </div>

      {/* Notes list */}
      {notes.length === 0 ? (
        <div className="notes-empty">{emptyMessage}</div>
      ) : (
        <div className="notes-list">
          {notes.map(note => (
            <div key={note.id} className="note-card">
              <div className="note-meta">
                {note.created_by && <span className="note-author">{note.created_by}</span>}
                <span className="note-date">{formatNoteDate(note.created_at)}</span>
              </div>
              <div
                className="note-body"
                dangerouslySetInnerHTML={{ __html: note.note || '<em>Empty note</em>' }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PersonModal({ person, detail, teams, registrations, loading, onClose, onNoteAdded, onLoadRegistrations }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [imgError, setImgError] = useState(false);
  const initials = `${(person.first_name || '')[0] || ''}${(person.last_name || '')[0] || ''}`.toUpperCase();

  const birthdate = detail?.birthdate;
  const age = calculateAge(birthdate);
  const notes = detail?.notes || [];
  const generalNotes = detail?.general_notes || [];
  const pastoralNotes = detail?.pastoral_notes || [];
  const membership = detail?.membership || person.membership || '';
  const inactive = typeof membership === 'string' && /inactive/i.test(membership);
  const socialLinks = detail?.social_links || {};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          {person.avatar && !imgError ? (
            <img className="modal-photo" src={person.avatar} alt="" onError={() => setImgError(true)} />
          ) : (
            <div className="modal-photo placeholder">{initials}</div>
          )}
          <div className="modal-info">
            <h2>{person.first_name} {person.last_name}</h2>
            {(birthdate || age !== null) && (
              <div className="modal-age-line">
                {age !== null && <span className="age-badge">Age {age}</span>}
                {birthdate && <span className="birthdate-text">{formatDate(birthdate)}</span>}
              </div>
            )}
            {detail && (socialLinks.facebook || socialLinks.instagram || socialLinks.linkedin) && (
              <div className="social-icons-row">
                {socialLinks.facebook && (
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="social-icon active" title="Facebook">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                )}
                {socialLinks.instagram && (
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="social-icon active" title="Instagram">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon active" title="LinkedIn">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </a>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {(detail?.on_volunteer_team || person.on_volunteer_team) && (
                <span className="modal-badge volunteer">Volunteer Team</span>
              )}
              {(detail?.has_membership_covenant || person.has_membership_covenant) && (
                <span className="modal-badge covenant">Membership Covenant</span>
              )}
              {inactive && (
                <span className="modal-badge inactive-badge">Inactive</span>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {inactive && (
          <div className="inactive-banner">
            <span className="inactive-banner-icon">⚠</span>
            Status: {membership}
          </div>
        )}

        <div className="modal-tabs">
          <button
            className={`modal-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`modal-tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            Growth Track {notes.length > 0 && <span className="tab-count">{notes.length}</span>}
          </button>
          <button
            className={`modal-tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General {generalNotes.length > 0 && <span className="tab-count">{generalNotes.length}</span>}
          </button>
          <button
            className={`modal-tab ${activeTab === 'pastoral' ? 'active' : ''}`}
            onClick={() => setActiveTab('pastoral')}
          >
            Pastoral Care {pastoralNotes.length > 0 && <span className="tab-count">{pastoralNotes.length}</span>}
          </button>
          <button
            className={`modal-tab ${activeTab === 'registrations' ? 'active' : ''}`}
            onClick={() => { setActiveTab('registrations'); if (onLoadRegistrations) onLoadRegistrations(person.id); }}
          >
            Registrations {Array.isArray(registrations) && registrations.length > 0 && <span className="tab-count">{registrations.length}</span>}
          </button>
        </div>

        {loading && (
          <div className="modal-body" style={{ textAlign: 'center', padding: '40px 28px' }}>
            <div className="spinner" />
          </div>
        )}

        {!loading && activeTab === 'profile' && (
          <div className="modal-body">
            {detail?.class_dates && (
              <div className="modal-section">
                <div className="modal-section-title">Growth Track</div>
                <div className="class-progress">
                  {[
                    { key: 'discovery_101', label: 'Discovery 101', cls: 'discovery' },
                    { key: 'serve_201', label: 'Serve 201', cls: 'serve' },
                    { key: 'values_301', label: 'Values 301', cls: 'values' },
                  ].map(c => {
                    const date = detail.class_dates[c.key];
                    const regDate = detail.reg_dates && detail.reg_dates[c.key];
                    return (
                      <div key={c.key} className={`class-step ${date ? `completed ${c.cls}` : regDate ? `registered ${c.cls}` : 'incomplete'}`}>
                        <div className="class-step-icon">{date ? '✓' : regDate ? '📋' : '○'}</div>
                        <div className="class-step-name">{c.label}</div>
                        <div className="class-step-date">
                          {date ? formatDate(date) : regDate ? `Registered ${formatDate(regDate)}` : '—'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="modal-section">
              <div className="modal-section-title">Serve Teams</div>
              {teams === null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Loading teams...
                </div>
              ) : teams.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Not assigned to any serve teams</div>
              ) : (
                <div className="teams-list">
                  {teams.map((t, i) => (
                    <div key={i} className="team-item">
                      <span className="team-name">{t.team_name}</span>
                      {t.positions.length > 0 && (
                        <span className="team-positions">{t.positions.join(', ')}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {detail?.emails?.length > 0 && (
              <div className="modal-section">
                <div className="modal-section-title">Email</div>
                {detail.emails.map((e, i) => (
                  <div key={i} className="contact-row">
                    <span className="contact-icon">✉</span>
                    <span className="contact-value">{e.address}</span>
                    {e.primary && <span className="contact-label">Primary</span>}
                  </div>
                ))}
              </div>
            )}

            {detail?.phones?.length > 0 && (
              <div className="modal-section">
                <div className="modal-section-title">Phone</div>
                {detail.phones.map((p, i) => (
                  <div key={i} className="contact-row">
                    <span className="contact-icon">☎</span>
                    <span className="contact-value">{p.number}</span>
                    {p.location && <span className="contact-label">{p.location}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'notes' && (
          <NotesTab
            notes={notes}
            emptyMessage="No Growth Track notes yet."
            personId={person.id}
            categoryKey="growth_track"
            onNoteAdded={onNoteAdded}
          />
        )}

        {!loading && activeTab === 'general' && (
          <NotesTab
            notes={generalNotes}
            emptyMessage="No General notes yet."
            personId={person.id}
            categoryKey="general"
            onNoteAdded={onNoteAdded}
          />
        )}

        {!loading && activeTab === 'pastoral' && (
          <NotesTab
            notes={pastoralNotes}
            emptyMessage="No Pastoral Care notes yet."
            personId={person.id}
            categoryKey="pastoral"
            onNoteAdded={onNoteAdded}
          />
        )}

        {!loading && activeTab === 'registrations' && (
          <div className="modal-body">
            {(registrations === null || registrations === 'loading') ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.95rem', justifyContent: 'center', padding: '32px 0' }}>
                <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Loading registrations...
              </div>
            ) : registrations.length === 0 ? (
              <div className="notes-empty">No upcoming registrations found.</div>
            ) : (
              <div className="registrations-list">
                {registrations.map((reg) => (
                  <div key={reg.event_id} className="registration-card">
                    {reg.logo_url && (
                      <img className="registration-logo" src={reg.logo_url} alt="" />
                    )}
                    <div className="registration-info">
                      <div className="registration-name">{reg.name}</div>
                      {reg.registered_at && (
                        <div className="registration-date">
                          Registered {formatDate(reg.registered_at)}
                        </div>
                      )}
                      {reg.open_at && (
                        <div className="registration-date">
                          Opens: {formatDate(reg.open_at)}
                        </div>
                      )}
                      {reg.close_at && (
                        <div className="registration-date">
                          Closes: {formatDate(reg.close_at)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PersonModal;
